// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { GanttVerticalMarker } from '@/billing/components/lifecycle-gantt-marker';
import { useObservedWidth } from '@/billing/hooks/use-observed-width';
import type { GanttAxis } from '@/billing/lifecycle/gantt-math';
import {
  GANTT_BAR_HEIGHT_PX,
  GANTT_BAR_VERTICAL_PADDING_PX,
  GANTT_MARKER_LABEL_BAND_PX,
  GANTT_ROW_HEIGHT_PX,
  MIN_BAR_WIDTH_PX,
  ZERO_WIDTH_HIT_TARGET_PX,
  ZERO_WIDTH_SOLID_PX,
  buildAxis,
  daysToPercent,
  shouldShowBarLabel,
} from '@/billing/lifecycle/gantt-math';
import type {
  PerInvoiceAxisBounds,
  PerInvoiceAxisRow,
} from '@/billing/lifecycle/per-invoice-rows';
import { buildPerInvoiceChart } from '@/billing/lifecycle/per-invoice-rows';
import { resolveBarColor } from '@/billing/lifecycle/resolve-bar-color';
import type { SegmentKey } from '@/billing/lifecycle/segments';
import { SEGMENTS } from '@/billing/lifecycle/segments';
import type { LifecycleRow } from '@/billing/lifecycle/transforms';
import type { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { Decimal } from 'decimal.js';
import { useState } from 'react';

// Renders the per-invoice Gantt chart inside the detail-table
// accordion. Delegates the row/axis computation to
// `buildPerInvoiceChart` and the layout to `PerInvoiceGanttLayout`.
export function PerInvoiceGantt(props: { row: LifecycleRow }) {
  const { row } = props;
  const chart = buildPerInvoiceChart(row);
  if (chart === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        No lifecycle data to chart for this invoice yet.
      </Typography>
    );
  }
  return (
    <PerInvoiceGanttLayout
      row={row}
      axisBounds={chart.axisBounds}
      axisRows={chart.axisRows}
      leftmostDate={chart.leftmostDate}
      rightmostDate={chart.rightmostDate}
      todayDay={chart.todayDay}
    />
  );
}

interface PerInvoiceGanttLayoutProps {
  row: LifecycleRow;
  axisBounds: PerInvoiceAxisBounds;
  axisRows: PerInvoiceAxisRow[];
  leftmostDate: string;
  rightmostDate: string;
  todayDay: number | null;
}

function PerInvoiceGanttLayout(props: PerInvoiceGanttLayoutProps) {
  const { row, axisBounds, axisRows, leftmostDate, rightmostDate, todayDay } =
    props;
  const theme = useTheme();
  const [containerRef, containerWidth] = useObservedWidth();
  const axis = buildAxis(axisBounds.axisMin, axisBounds.axisMax);
  const [hoveredKey, setHoveredKey] = useState<SegmentKey | null>(null);
  const hoveredRow =
    hoveredKey === null
      ? null
      : (axisRows.find((r) => r.key === hoveredKey) ?? null);
  const isPaid = row.paidDate !== null;
  // Per-segment carrying costs — the tooltip for each bar shows this
  // invoice's contribution for that segment alongside the day count.
  const costByKey: Record<SegmentKey, Decimal | null> = {
    chargeLag: row.carryingCostBreakdown.chargeLag,
    invoiceReview: row.carryingCostBreakdown.invoiceReview,
    deliveryLag: row.carryingCostBreakdown.deliveryLag,
    dateLag: null,
    actualTerms: row.carryingCostBreakdown.actualTerms,
    delinquency: row.carryingCostBreakdown.delinquency,
    totalCycle: null,
  };
  return (
    <Box>
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          paddingTop: `${GANTT_MARKER_LABEL_BAND_PX}px`,
          paddingBottom: `${GANTT_MARKER_LABEL_BAND_PX}px`,
          overflow: 'hidden',
        }}
      >
        {axisRows.map((rowSpec) => {
          const segmentDef = SEGMENTS.find((s) => s.key === rowSpec.key);
          if (segmentDef === undefined) return null;
          if (rowSpec.startDay === null || rowSpec.endDay === null) return null;
          const cost = costByKey[rowSpec.key];
          return (
            <PerInvoiceGanttBar
              key={rowSpec.key}
              label={segmentDef.label}
              color={resolveBarColor(theme, segmentDef.barColor)}
              startDay={rowSpec.startDay}
              endDay={rowSpec.endDay}
              displayValue={rowSpec.displayValue}
              startDate={rowSpec.startDate}
              endDate={rowSpec.endDate}
              axis={axis}
              axisWidthPx={containerWidth}
              paid={isPaid}
              carryingCost={cost}
              currencyCode={row.currency}
              onHoverStart={() => setHoveredKey(rowSpec.key)}
              onHoverEnd={() => setHoveredKey(null)}
            />
          );
        })}
        {/* Connectors — 1px dotted vertical lines spanning only the
            inter-row whitespace (bottom of bar N to top of bar N+1) at
            the shared anchor x-position. Every row in axisRows is
            placeable at this point, so the connectors just run row to
            row. */}
        {axisRows.slice(0, -1).map((rowSpec, idx) => {
          const nextRowSpec = axisRows[idx + 1];
          if (nextRowSpec === undefined) return null;
          if (rowSpec.endDay === null) return null;
          const xPct = daysToPercent(rowSpec.endDay, axis);
          const topPx =
            GANTT_MARKER_LABEL_BAND_PX +
            idx * GANTT_ROW_HEIGHT_PX +
            GANTT_BAR_VERTICAL_PADDING_PX +
            GANTT_BAR_HEIGHT_PX;
          const heightPx = 2 * GANTT_BAR_VERTICAL_PADDING_PX;
          return (
            <Box
              key={`connector-${rowSpec.key}`}
              sx={{
                position: 'absolute',
                left: `${xPct}%`,
                top: `${topPx}px`,
                width: '1px',
                height: `${heightPx}px`,
                transform: 'translateX(-50%)',
                backgroundImage: `linear-gradient(to bottom, ${theme.palette.grey[400]} 50%, transparent 50%)`,
                backgroundSize: '1px 3px',
                backgroundRepeat: 'repeat-y',
                pointerEvents: 'none',
              }}
            />
          );
        })}
        {/* Today marker — dotted vertical line spanning the axis body
            when the timeline intersects the current date. The "Today"
            label rides in the top marker-label band; it's hidden while
            a row is hovered so it doesn't overlap with the hover-start
            label that renders in the same band. */}
        {todayDay !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: `${GANTT_MARKER_LABEL_BAND_PX}px`,
              bottom: `${GANTT_MARKER_LABEL_BAND_PX}px`,
              left: `${daysToPercent(todayDay, axis)}%`,
              width: '1px',
              transform: 'translateX(-50%)',
              backgroundImage: `linear-gradient(to bottom, ${theme.palette.text.secondary} 50%, transparent 50%)`,
              backgroundSize: '1px 4px',
              backgroundRepeat: 'repeat-y',
              pointerEvents: 'none',
            }}
          >
            {hoveredKey === null && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  top: `${-GANTT_MARKER_LABEL_BAND_PX + 2}px`,
                  transform: `translateX(${(-daysToPercent(todayDay, axis)).toFixed(2)}%)`,
                  whiteSpace: 'nowrap',
                  color: 'text.secondary',
                  fontSize: 10,
                  fontWeight: 600,
                  backgroundColor: 'background.paper',
                  padding: '0 4px',
                }}
              >
                Today
              </Typography>
            )}
          </Box>
        )}
        {/* Hover markers — when a bar is hovered, draw transient vertical
            markers at its start and end anchors. Start label rides above
            the axis, end label below. Markers use solid lines in the
            hovered segment's color so the visual connection to the bar
            is unambiguous. */}
        {hoveredRow !== null &&
          hoveredRow.startDay !== null &&
          hoveredRow.startLabel !== null && (
            <GanttVerticalMarker
              leftPct={daysToPercent(hoveredRow.startDay, axis)}
              color={resolveBarColor(
                theme,
                SEGMENTS.find((s) => s.key === hoveredRow.key)?.barColor ??
                  'primary.main'
              )}
              variant="solid"
              labelText={hoveredRow.startLabel}
              labelPosition="top"
            />
          )}
        {hoveredRow !== null &&
          hoveredRow.endDay !== null &&
          hoveredRow.endLabel !== null && (
            <GanttVerticalMarker
              leftPct={daysToPercent(hoveredRow.endDay, axis)}
              color={resolveBarColor(
                theme,
                SEGMENTS.find((s) => s.key === hoveredRow.key)?.barColor ??
                  'primary.main'
              )}
              variant="solid"
              labelText={hoveredRow.endLabel}
              labelPosition="bottom"
            />
          )}
      </Box>
      {/* Bottom date-range line — formatted calendar dates at each axis
          endpoint with a horizontal line between. Matches the aggregate
          Gantt's "0 days / N days" footer in visual weight. */}
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {leftmostDate}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'text.primary' }} />
        <Typography variant="caption" color="text.secondary">
          {rightmostDate}
        </Typography>
      </Box>
    </Box>
  );
}

interface PerInvoiceGanttBarProps {
  label: string;
  color: string;
  startDay: number;
  endDay: number;
  displayValue: string;
  startDate: string | null;
  endDate: string | null;
  axis: GanttAxis;
  axisWidthPx: number;
  paid: boolean;
  carryingCost: Decimal | null;
  currencyCode: IsoCurrencyCode;
  onHoverStart: () => void;
  onHoverEnd: () => void;
}

function PerInvoiceGanttBar(props: PerInvoiceGanttBarProps) {
  const {
    label,
    color,
    startDay,
    endDay,
    displayValue,
    startDate,
    endDate,
    axis,
    axisWidthPx,
    paid,
    carryingCost,
    currencyCode: isoCode,
    onHoverStart,
    onHoverEnd,
  } = props;
  const leftPct = daysToPercent(Math.min(startDay, endDay), axis);
  const widthPct = daysToPercent(Math.max(startDay, endDay), axis) - leftPct;
  const showLabel = shouldShowBarLabel(widthPct, axisWidthPx, displayValue);
  return (
    <Box
      sx={{
        position: 'relative',
        height: GANTT_BAR_HEIGHT_PX + 8,
        display: 'flex',
        alignItems: 'center',
      }}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
    >
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          height: GANTT_BAR_HEIGHT_PX,
          backgroundColor: 'action.hover',
        }}
      >
        <Tooltip
          title={
            <>
              {label}: {displayValue}
              {startDate !== null && (
                <>
                  <br />
                  Start: {startDate}
                </>
              )}
              {endDate !== null && (
                <>
                  <br />
                  End: {endDate}
                </>
              )}
              {carryingCost !== null && (
                <>
                  <br />
                  Carrying {carryingCost.lessThan(0) ? 'gain' : 'cost'}:{' '}
                  {formatCurrencyValue({ amount: carryingCost.abs(), isoCode })}
                </>
              )}
            </>
          }
          arrow
        >
          {widthPct === 0 ? (
            // Zero-day segment: 1px solid column centered inside a
            // larger transparent hit area so the tooltip still has a
            // hoverable target.
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${leftPct}%`,
                width: `${ZERO_WIDTH_HIT_TARGET_PX}px`,
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: paid ? 1 : 0.7,
              }}
            >
              <Box
                sx={{
                  width: `${ZERO_WIDTH_SOLID_PX}px`,
                  height: '100%',
                  backgroundColor: color,
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                minWidth: `${MIN_BAR_WIDTH_PX}px`,
                backgroundColor: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: paid ? 1 : 0.7,
                overflow: 'hidden',
              }}
            >
              {showLabel && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'common.white',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    px: 0.5,
                  }}
                >
                  {displayValue}
                </Typography>
              )}
            </Box>
          )}
        </Tooltip>
      </Box>
    </Box>
  );
}
